import { PWA_ENABLED } from "../config/variant";
import { isBeachPopCacheName, isBeachPopServiceWorkerUrl, shouldCleanupLegacyPwa, shouldRegisterServiceWorker } from "./policy";

export interface PwaCleanupResult {
  unregistered: number;
  cachesDeleted: number;
}

export interface PwaCleanupHost {
  getRegistrations(): Promise<Array<{ scriptURL: string; unregister(): Promise<boolean> }>>;
  cacheKeys(): Promise<string[]>;
  deleteCache(name: string): Promise<boolean>;
}

export function browserCleanupHost(): PwaCleanupHost | null {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null;
  return {
    async getRegistrations() {
      const registrations = await navigator.serviceWorker.getRegistrations();
      return registrations.map((registration) => ({
        scriptURL: registration.active?.scriptURL ?? registration.waiting?.scriptURL ?? registration.installing?.scriptURL ?? "",
        unregister: () => registration.unregister(),
      }));
    },
    async cacheKeys() {
      if (typeof caches === "undefined") return [];
      return caches.keys();
    },
    async deleteCache(name: string) {
      if (typeof caches === "undefined") return false;
      return caches.delete(name);
    },
  };
}

/** Unregisters Beach Pop service workers and deletes Beach Pop caches only. Never touches localStorage. */
export async function cleanupLegacyBeachPopPwa(
  pwaEnabled = PWA_ENABLED,
  host: PwaCleanupHost | null = browserCleanupHost(),
): Promise<PwaCleanupResult> {
  const result: PwaCleanupResult = { unregistered: 0, cachesDeleted: 0 };
  if (!shouldCleanupLegacyPwa(pwaEnabled) || !host) return result;

  const registrations = await host.getRegistrations();
  for (const registration of registrations) {
    if (!isBeachPopServiceWorkerUrl(registration.scriptURL)) continue;
    const removed = await registration.unregister();
    if (removed) result.unregistered += 1;
  }

  const keys = await host.cacheKeys();
  for (const key of keys) {
    if (!isBeachPopCacheName(key)) continue;
    const removed = await host.deleteCache(key);
    if (removed) result.cachesDeleted += 1;
  }

  return result;
}

export async function registerBeachPopServiceWorker(
  pwaEnabled = PWA_ENABLED,
  register: ((url: string) => Promise<ServiceWorkerRegistration>) | null = typeof navigator !== "undefined" && "serviceWorker" in navigator
    ? (url) => navigator.serviceWorker.register(url)
    : null,
): Promise<ServiceWorkerRegistration | null> {
  if (!shouldRegisterServiceWorker(pwaEnabled) || !register) return null;
  return register("./sw.js");
}

export function applyVariantRuntime(): void {
  window.addEventListener("load", () => {
    if (shouldRegisterServiceWorker(PWA_ENABLED)) {
      void registerBeachPopServiceWorker();
      return;
    }
    void cleanupLegacyBeachPopPwa();
  });
}
